import React, { useEffect, useState, useCallback } from 'react'
import {
  Box, Button, Checkbox, CircularProgress, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Tooltip, Typography
} from '@material-ui/core'
import VisibilityIcon from '@material-ui/icons/Visibility'
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff'
import PersonAddIcon from '@material-ui/icons/PersonAdd'
import axios from 'axios'

const getInitials = (name) => {
  if (!name) return '?'
  const parts = name.trim().split(/\s+/)
  return parts.map(p => p[0]).slice(0, 2).join('').toUpperCase()
}

const ContactRow = ({ name, phone, email, source, isVisible, onToggleVisible }) => (
  <Box
    display="flex"
    alignItems="center"
    style={{
      gap: 8,
      padding: '6px 4px',
      borderBottom: '1px solid #f5f5f5',
      background: isVisible ? 'rgba(76,175,80,0.04)' : 'transparent',
      borderRadius: 4
    }}
  >
    <div style={{
      width: 28, height: 28,
      background: isVisible ? '#4caf50' : '#ccc',
      borderRadius: '50%',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: '#fff', fontSize: 10, fontWeight: 700, flexShrink: 0
    }}>
      {getInitials(name)}
    </div>
    <div style={{ flex: 1 }}>
      <Typography variant="body2" style={{ fontWeight: isVisible ? 600 : 400, color: '#333', fontSize: 12 }}>
        {name || 'Unknown'}
        {isVisible && (
          <span style={{ fontSize: 9, background: '#e8f5e9', color: '#2e7d32', padding: '1px 5px', borderRadius: 3, marginLeft: 4 }}>
            VISIBLE TO SUB
          </span>
        )}
        {source && (
          <span style={{ fontSize: 9, color: '#999', marginLeft: 4 }}>({source})</span>
        )}
      </Typography>
      <Typography variant="caption" style={{ color: '#888' }}>
        {[phone, email].filter(Boolean).join(' · ') || 'No contact info'}
      </Typography>
    </div>
    {onToggleVisible && (
      <Tooltip title={isVisible ? 'Hide from sub' : 'Show to sub'}>
        <Checkbox
          checked={isVisible}
          onChange={onToggleVisible}
          icon={<VisibilityOffIcon style={{ fontSize: 18, color: '#ccc' }} />}
          checkedIcon={<VisibilityIcon style={{ fontSize: 18, color: '#4caf50' }} />}
          size="small"
          style={{ padding: 4 }}
        />
      </Tooltip>
    )}
  </Box>
)

const ContactsSection = ({ url, scheduleItemId, preferredContact, visibleContacts: visibleContactsProp, clientId }) => {
  const [portalContacts, setPortalContacts] = useState([])
  const [loading, setLoading] = useState(false)
  const [visibleIds, setVisibleIds] = useState(new Set((visibleContactsProp || []).map(String)))
  const [addDialogOpen, setAddDialogOpen] = useState(false)
  const [newContact, setNewContact] = useState({ firstName: '', lastName: '', email: '', phone: '', notes: '' })
  const [saving, setSaving] = useState(false)

  // Sync visible contacts when prop changes (different item selected)
  useEffect(() => {
    setVisibleIds(new Set((visibleContactsProp || []).map(String)))
  }, [scheduleItemId, visibleContactsProp])

  const fetchPortalContacts = useCallback(async () => {
    if (!scheduleItemId) return
    setLoading(true)
    try {
      const resp = await axios.get(`${url}/api/herbicide-schedule/${scheduleItemId}/contacts`)
      const data = resp.data
      setPortalContacts(Array.isArray(data) ? data : [])
    } catch (err) {
      setPortalContacts([])
    }
    setLoading(false)
  }, [url, scheduleItemId])

  useEffect(() => { fetchPortalContacts() }, [fetchPortalContacts])

  const handleToggleVisible = async (contactId) => {
    const id = String(contactId)
    const newVisible = new Set(visibleIds)
    if (newVisible.has(id)) {
      newVisible.delete(id)
    } else {
      newVisible.add(id)
    }
    setVisibleIds(newVisible)

    try {
      await axios.put(`${url}/api/herbicide-schedule/${scheduleItemId}/visible-contacts`, {
        contactIds: [...newVisible]
      })
    } catch (err) {
      console.error('Error updating visible contacts:', err)
    }
  }

  const handleAddContact = async () => {
    if (!newContact.firstName && !newContact.lastName) return
    setSaving(true)
    try {
      await axios.post(`${url}/api/herbicide-schedule/${scheduleItemId}/contacts`, newContact)
      setAddDialogOpen(false)
      setNewContact({ firstName: '', lastName: '', email: '', phone: '', notes: '' })
      await fetchPortalContacts()
    } catch (err) {
      console.error('Error creating contact:', err)
    }
    setSaving(false)
  }

  const hasAspireContact = preferredContact?.name || preferredContact?.phone || preferredContact?.email
  const visibleCount = visibleIds.size

  // Sort: visible-to-sub contacts first, then alphabetical
  const sortedContacts = [...portalContacts].sort((a, b) => {
    const aVis = visibleIds.has(String(a._id)) ? 0 : 1
    const bVis = visibleIds.has(String(b._id)) ? 0 : 1
    if (aVis !== bVis) return aVis - bVis
    const aName = [a.firstName, a.lastName].filter(Boolean).join(' ')
    const bName = [b.firstName, b.lastName].filter(Boolean).join(' ')
    return aName.localeCompare(bName)
  })

  return (
    <div>
      {/* Header with visible count */}
      {visibleCount > 0 && (
        <Box mb={0.5}>
          <Typography variant="caption" style={{ color: '#2e7d32', fontWeight: 600, fontSize: 10 }}>
            {visibleCount} contact{visibleCount !== 1 ? 's' : ''} visible to sub
          </Typography>
        </Box>
      )}

      {/* Aspire preferred contact */}
      {hasAspireContact && (
        <ContactRow
          name={preferredContact.name}
          phone={preferredContact.phone}
          email={preferredContact.email}
          source="Aspire"
          isVisible={false}
        />
      )}

      {/* Portal contacts with visibility toggle */}
      {loading ? (
        <Box display="flex" justifyContent="center" p={1}><CircularProgress size={16} /></Box>
      ) : (
        <div style={{ maxHeight: 160, overflowY: 'auto' }}>
          {sortedContacts.map(contact => {
            const name = [contact.firstName, contact.lastName].filter(Boolean).join(' ')
            const phone = (contact.phoneNumbers || [])[0]?.number || ''
            return (
              <ContactRow
                key={contact._id}
                name={name}
                phone={phone}
                email={contact.email}
                source="Portal"
                isVisible={visibleIds.has(String(contact._id))}
                onToggleVisible={() => handleToggleVisible(contact._id)}
              />
            )
          })}
        </div>
      )}

      {!hasAspireContact && !portalContacts.length && !loading && (
        <Typography variant="body2" color="textSecondary" style={{ fontSize: 12, fontStyle: 'italic' }}>
          No contacts on file.
        </Typography>
      )}

      {/* Add Contact button */}
      <Box mt={1}>
        <Button
          size="small"
          startIcon={<PersonAddIcon />}
          onClick={() => setAddDialogOpen(true)}
          style={{ fontSize: 11, color: '#1976d2', textTransform: 'none' }}
        >
          Add Contact
        </Button>
      </Box>

      {/* Add Contact Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Contact</DialogTitle>
        <DialogContent>
          <Box display="flex" style={{ gap: 12 }}>
            <TextField
              label="First Name"
              value={newContact.firstName}
              onChange={(e) => setNewContact({ ...newContact, firstName: e.target.value })}
              variant="outlined"
              size="small"
              fullWidth
              margin="dense"
            />
            <TextField
              label="Last Name"
              value={newContact.lastName}
              onChange={(e) => setNewContact({ ...newContact, lastName: e.target.value })}
              variant="outlined"
              size="small"
              fullWidth
              margin="dense"
            />
          </Box>
          <TextField
            label="Email"
            value={newContact.email}
            onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
            variant="outlined"
            size="small"
            fullWidth
            margin="dense"
          />
          <TextField
            label="Phone"
            value={newContact.phone}
            onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })}
            variant="outlined"
            size="small"
            fullWidth
            margin="dense"
          />
          <TextField
            label="Notes (optional)"
            value={newContact.notes}
            onChange={(e) => setNewContact({ ...newContact, notes: e.target.value })}
            variant="outlined"
            size="small"
            fullWidth
            margin="dense"
            multiline
            rows={2}
          />
          <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginTop: 4 }}>
            New contact will be saved to the customer record and auto-marked as visible to the sub.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleAddContact}
            color="primary"
            variant="contained"
            disabled={saving || (!newContact.firstName && !newContact.lastName)}
          >
            {saving ? 'Saving...' : 'Add Contact'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

export default ContactsSection
